import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { PlusIcon, TrashIcon, PencilIcon, ChartBarIcon, ClockIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { DocumentIcon } from '@heroicons/react/24/outline';
import { FaTag } from "react-icons/fa6";
import axios from 'axios'; // Added axios import

// Import extracted components
import MemberAvatarStack from './components/MemberAvatarStack';
import ChatList from './components/ChatList';

// Import hooks
import { useProject } from './hooks/useProject';
import { useChats } from './hooks/useChats';
import { useTags } from './hooks/useTags';
import { useDocuments } from './hooks/useDocuments';

// Import utilities
import { truncateText, formatFileSize, getStatusColor, getCategoryIcon, getTagIcon } from './utils/helpers';
import { tagColors, tagIcons } from './utils/constants';

// Import external components
import ProjectSettingsModal from '../../components/projects/ProjectSettingsModal';
import ContextMenu from '../../components/common/ContextMenu';
import Modal from '../../components/common/Modal';

// Function to get proxied image URL for expired S3 URLs
const getProxiedImageUrl = (s3Url) => {
  if (!s3Url) return null;
  
  // If it's already not an S3 URL, just return it as is
  if (!s3Url.includes('amazonaws.com')) {
    return s3Url;
  }
  
  // Extract file key from the S3 URL (everything after the bucket name)
  const urlParts = s3Url.split('/');
  const fileKey = urlParts.slice(3).join('/');
  
  // Return URL that will be handled by our backend proxy endpoint
  return `${process.env.REACT_APP_API_URL}/brand/image-proxy?key=${encodeURIComponent(fileKey)}`;
};

const ProjectDashboard = ({ user }) => {
  const { projectId } = useParams();
  const [error, setError] = useState('');
  const [filteredChats, setFilteredChats] = useState([]);
  
  // 🚀 NEW: Context bucket state
  const [contextBucket, setContextBucket] = useState(null);
  const [contextLoading, setContextLoading] = useState(false);
  const [showContextDetails, setShowContextDetails] = useState(false);
  const [contextError, setContextError] = useState('');

  // Use all the extracted hooks
  const {
    project,
    loading,
    error: projectError,
    showSettingsModal,
    setShowSettingsModal,
    handleProjectUpdated
  } = useProject(projectId);

  const {
    chats,
    setChats,
    isCreatingChat,
    typingUsers,
    activeMenu,
    setActiveMenu,
    deleteModalOpen,
    chatToDelete,
    editingChatId,
    editValue,
    setEditValue,
    setEditingChatId,
    menuPosition,
    setDeleteModalOpen,
    refreshChats,
    createNewChat,
    handleChatClick,
    handleMenuClick,
    handleRename,
    handleRenameSubmit,
    handleDelete,
    confirmDelete
  } = useChats(projectId, user);

  const {
    projectTags,
    showTagModal,
    selectedChatForTagging,
    isCreatingNewTag,
    newTagName,
    newTagColor,
    newTagIcon,
    showFilterMenu,
    filterMenuPosition,
    selectedTagFilter,
    fetchProjectTags,
    removeTagFromChat: removeTagFromChatAPI,
    handleTagClick,
    handleTagSelection,
    handleCreateNewTag,
    handleFilterByTag,
    handleFilterMenuClick,
    clearFilter,
    setShowTagModal,
    setSelectedChatForTagging,
    setIsCreatingNewTag,
    setNewTagName,
    setNewTagColor,
    setNewTagIcon,
    setShowFilterMenu
  } = useTags(projectId);

  const {
    documents,
    documentsLoading,
    uploadingDocument,
    documentError,
    selectedFiles,
    showDocuments,
    documentCategory,
    documentToDelete,
    deleteDocumentModalOpen,
    fetchDocuments,
    handleDocumentUpload,
    handleDeleteDocument,
    setDocumentError,
    setSelectedFiles,
    setShowDocuments,
    setDocumentCategory,
    setDocumentToDelete,
    setDeleteDocumentModalOpen
  } = useDocuments(projectId);

  // 🚀 NEW: Fetch project context bucket
  const fetchContextBucket = async () => {
    if (!projectId) return;
    
    setContextLoading(true);
    setContextError('');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/project-context/${projectId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch context bucket');
      }
      
      const data = await response.json();
      setContextBucket(data.contextBucket);
    } catch (error) {
      console.error('Error fetching context bucket:', error);
      setContextError(error.message);
    } finally {
      setContextLoading(false);
    }
  };

  // 🚀 NEW: Update project description in context bucket
  const updateProjectDescription = async (description) => {
    if (!projectId) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/project-context/${projectId}/description`,
        { description, generateEnhanced: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update context bucket state
      setContextBucket(prev => prev ? {
        ...prev,
        projectDescription: response.data.projectDescription,
        enhancedDescription: response.data.enhancedDescription,
        metadata: { ...prev.metadata, totalTokens: response.data.totalTokens }
      } : null);
      
      return response.data;
    } catch (error) {
      console.error('Error updating project description in context:', error);
      // Don't throw - this is supplementary to the main project update
    }
  };

  // Load initial data
  useEffect(() => {
    if (projectId) {
      Promise.all([
        refreshChats(),
        fetchProjectTags(),
        fetchDocuments(),
        fetchContextBucket() // 🚀 NEW: Fetch context bucket
      ]).catch(error => {
        console.warn('Some data failed to load:', error);
      });
    }
  }, [projectId]);

  // Listen for project chat creation events
  useEffect(() => {
    const handleProjectChatCreated = (event) => {
      const { projectId: eventProjectId } = event.detail;
      if (eventProjectId === projectId) {
        console.log('New project chat created, refreshing chat list');
        refreshChats();
      }
    };

    window.addEventListener('projectChatCreated', handleProjectChatCreated);
    
    return () => {
      window.removeEventListener('projectChatCreated', handleProjectChatCreated);
    };
  }, [projectId, refreshChats]);

  // Update filtered chats when chats or filter changes
  useEffect(() => {
    if (!selectedTagFilter) {
      setFilteredChats(chats);
    } else {
      const filtered = chats.filter(chat => 
        chat.tags && chat.tags.some(tag => tag.id === selectedTagFilter)
      );
      setFilteredChats(filtered);
    }
  }, [chats, selectedTagFilter]);

  // Add keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Check for Command (Mac) + K for new chat
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        createNewChat();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isCreatingChat, createNewChat]);

  // Handle tag removal from chat
  const removeTagFromChat = async (chatId, tagId) => {
    try {
      await removeTagFromChatAPI(chatId, tagId);
      
      // Update local chat state
      setChats(prevChats => {
        return prevChats.map(chat => {
          if (chat._id === chatId) {
            const updatedTags = chat.tags ? chat.tags.filter(tag => tag.id !== tagId) : [];
            return { ...chat, tags: updatedTags };
          }
          return chat;
        });
      });
    } catch (error) {
      setError('Failed to remove tag from chat');
    }
  };

  // Handle tag assignment to chat
  const assignTagToChat = async (chatId, tagId, tagObject = null) => {
    try {
      // Update local chat state
      setChats(prevChats => {
        return prevChats.map(chat => {
          if (chat._id === chatId) {
            // Use the provided tag object or find it in projectTags
            const tag = tagObject || projectTags.find(t => t.id === tagId);
            if (!tag) {
              console.error('Tag not found for assignment:', tagId);
              return chat;
            }
            const updatedTags = chat.tags ? [...chat.tags, tag] : [tag];
            return { ...chat, tags: updatedTags };
          }
          return chat;
        });
      });
    } catch (error) {
      console.error('Error updating chat tags:', error);
    }
  };

  // Enhanced tag handlers that update local state
  const handleTagSelectionWithState = async (tagId) => {
    try {
      const tag = await handleTagSelection(tagId);
      if (tag && selectedChatForTagging) {
        await assignTagToChat(selectedChatForTagging, tagId, tag);
      }
    } catch (error) {
      setError('Failed to assign tag to chat');
    }
  };

  const handleCreateNewTagWithState = async () => {
    try {
      const newTag = await handleCreateNewTag();
      if (newTag && selectedChatForTagging) {
        await assignTagToChat(selectedChatForTagging, newTag.id, newTag);
      }
    } catch (error) {
      setError('Failed to create and assign tag');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black dark:border-white"></div>
      </div>
    );
  }

  if (error || projectError || !project) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-red-500 dark:text-red-400">{error || projectError || 'Project not found'}</p>
          <button 
            onClick={() => window.history.back()}
            className="mt-4 px-4 py-2 bg-black dark:bg-white text-white dark:text-black rounded-lg hover:bg-[#181818] dark:hover:bg-[#EDEDED] transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-white dark:bg-[#111111] text-black dark:text-white">
      <div className="max-w-4xl mx-auto p-4 pt-8">
        {/* Project Header */}
        <div className="mb-6">
          <div className="bg-white dark:bg-[#111111] rounded-3xl p-6 mx-4 border border-[#C3C3C3] dark:border-[#444444] relative">
            <div className="mb-6">
              <div className="flex items-center gap-2 text-sm text-[#999999] dark:text-[#999999] mb-2">
                <span>Description</span>
              </div>
              <div 
                dangerouslySetInnerHTML={{ __html: truncateText(project.description, 300) }} 
                className="text-[#181818] dark:text-[#EDEDED] leading-relaxed" 
              />
            </div>

            {/* 🚀 NEW: Context & Memory Section */}
            <div 
              className="flex items-center justify-between text-[#999999] dark:text-[#999999] cursor-pointer hover:text-[#181818] dark:hover:text-[#EDEDED] transition-colors"
              onClick={() => setShowContextDetails(!showContextDetails)}
            >
              <div className="flex items-center gap-2">
                <ChartBarIcon className="w-4 h-4" />
                <span className="text-sm">Context & Memory</span>
                {contextBucket && (
                  <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-0.5 rounded-full">
                    {contextBucket.metadata.totalTokens} tokens
                  </span>
                )}
              </div>
              <svg 
                className={`w-4 h-4 ml-2 transition-transform ${showContextDetails ? 'rotate-90' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>

            {/* Context Details Panel */}
            {showContextDetails && (
              <div className="mt-4 pt-4 border-t border-[#C3C3C3] dark:border-[#444444]">
                {contextLoading ? (
                  <div className="text-center py-4">
                    <div className="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-[#181818] dark:border-[#EDEDED]"></div>
                    <p className="mt-2 text-sm text-[#999999] dark:text-[#999999]">Loading context...</p>
                  </div>
                ) : contextError ? (
                  <div className="text-center py-4">
                    <p className="text-sm text-red-600 dark:text-red-400">{contextError}</p>
                    <button 
                      onClick={fetchContextBucket}
                      className="mt-2 text-sm text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      Retry
                    </button>
                  </div>
                ) : contextBucket ? (
                  <div className="space-y-4">
                    {/* Enhanced Description */}
                    {contextBucket.enhancedDescription && contextBucket.enhancedDescription !== contextBucket.projectDescription && (
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <DocumentTextIcon className="w-4 h-4 text-[#999999]" />
                          <span className="text-sm font-medium text-[#181818] dark:text-[#EDEDED]">AI-Enhanced Overview</span>
                        </div>
                        <p className="text-sm text-[#666666] dark:text-[#CCCCCC] leading-relaxed">
                          {truncateText(contextBucket.enhancedDescription, 200)}
                        </p>
                      </div>
                    )}

                    {/* File Context */}
                    {contextBucket.fileContext.totalFiles > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <DocumentIcon className="w-4 h-4 text-[#999999]" />
                          <span className="text-sm font-medium text-[#181818] dark:text-[#EDEDED]">
                            Files ({contextBucket.fileContext.totalFiles})
                          </span>
                        </div>
                        {contextBucket.fileContext.summary && (
                          <p className="text-sm text-[#666666] dark:text-[#CCCCCC] leading-relaxed">
                            {truncateText(contextBucket.fileContext.summary, 150)}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Conversation Context */}
                    {contextBucket.conversationContext.chatCount > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <ClockIcon className="w-4 h-4 text-[#999999]" />
                          <span className="text-sm font-medium text-[#181818] dark:text-[#EDEDED]">
                            Conversations ({contextBucket.conversationContext.chatCount})
                          </span>
                        </div>
                        {contextBucket.conversationContext.globalSummary && (
                          <p className="text-sm text-[#666666] dark:text-[#CCCCCC] leading-relaxed">
                            {truncateText(contextBucket.conversationContext.globalSummary, 150)}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Context Stats */}
                    <div className="flex items-center justify-between pt-2 border-t border-[#E5E7EB] dark:border-[#333333]">
                      <div className="flex items-center gap-4 text-xs text-[#999999]">
                        <span>Total: {contextBucket.metadata.totalTokens} tokens</span>
                        {contextBucket.conversationContext.lastUpdated && (
                          <span>
                            Updated: {new Date(contextBucket.conversationContext.lastUpdated).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                      <button
                        onClick={fetchContextBucket}
                        className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        Refresh
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-sm text-[#999999] dark:text-[#999999] mb-2">
                      No context data available yet
                    </p>
                    <p className="text-xs text-[#999999] dark:text-[#999999]">
                      Upload documents and start conversations to build project context
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Documents Section */}
        <div className="mb-6">
          {showDocuments && (
            <div className="px-4 mb-4">
              <div className="flex items-center gap-2 mb-3">
                <label className="text-sm text-[#999999] dark:text-[#999999]">
                  Upload Category:
                </label>
                <select
                  value={documentCategory}
                  onChange={(e) => setDocumentCategory(e.target.value)}
                  className="px-3 py-1 text-sm border border-[#C3C3C3] dark:border-[#444444] rounded-lg bg-white dark:bg-[#161616] text-[#181818] dark:text-[#EDEDED] focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="requirements">📋 Requirements</option>
                  <option value="design">🎨 Design</option>
                  <option value="technical">⚙️ Technical</option>
                  <option value="business">📊 Business</option>
                  <option value="research">🔬 Research</option>
                  <option value="other">📄 Other</option>
                </select>
              </div>

              {/* Error Display */}
              {documentError && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-sm text-red-600 dark:text-red-400">{documentError}</p>
                </div>
              )}

              {/* Documents List */}
              {documentsLoading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-[#181818] dark:border-[#EDEDED]"></div>
                  <p className="mt-2 text-sm text-[#999999] dark:text-[#999999]">Loading documents...</p>
                </div>
              ) : documents.length === 0 ? (
                <div className="text-center py-8 text-[#999999] dark:text-[#999999]">
                  <DocumentIcon className="w-12 h-12 mx-auto mb-3 text-[#C3C3C3] dark:text-[#444444]" />
                  <p className="text-lg font-medium mb-2">No Documents Yet</p>
                  <p className="text-sm">Upload documents to enhance your project's AI context</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {documents.map((doc) => (
                    <div
                      key={doc.id}
                      className="flex items-center justify-between p-4 bg-[#EDEDED] dark:bg-[#161616] rounded-xl border border-[#C3C3C3] dark:border-[#444444]"
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <div className="text-2xl">
                          {getCategoryIcon(doc.category)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-[#181818] dark:text-[#EDEDED] truncate">
                              {doc.name}
                            </h4>
                            <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(doc.status)}`}>
                              {doc.status}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-sm text-[#999999] dark:text-[#999999]">
                            <span>{formatFileSize(doc.fileSize)}</span>
                            <span className="capitalize">{doc.category}</span>
                            {doc.processingResults?.chunksCreated > 0 && (
                              <span>{doc.processingResults.chunksCreated} chunks</span>
                            )}
                            {doc.processingResults?.processingTime && (
                              <span>
                                Processed in {Math.round(doc.processingResults.processingTime / 1000)}s
                              </span>
                            )}
                          </div>
                          {doc.errorMessage && (
                            <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                              {doc.errorMessage}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {project.isAdmin && (
                          <button
                            onClick={() => {
                              setDocumentToDelete(doc);
                              setDeleteDocumentModalOpen(true);
                            }}
                            className="p-2 text-[#999999] hover:text-red-600 dark:hover:text-red-400 rounded-lg hover:bg-[#EDEDED] dark:hover:bg-[#161616]"
                            title="Delete document"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Chats Section */}
        <div className="flex flex-col h-[calc(100vh-400px)]">
          {/* Action Buttons */}
          <div className="flex items-center gap-3 mb-8 px-4">
            <button
              onClick={createNewChat}
              disabled={isCreatingChat}
              className="flex-1 flex items-center justify-center text-md gap-2 px-4 py-3 border border-[#E5E7EB] dark:border-[#444444] text-black dark:text-white rounded-2xl hover:bg-[#F9FAFB] dark:hover:bg-[#181818] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex items-center gap-2">
                <PlusIcon className="w-4 h-4" />
                {isCreatingChat ? 'Creating...' : 'New Chat'}
              </div>
              <div className="flex items-center gap-1 ml-3">
                <div className="px-1 text-[16px] border border-[#E5E7EB] dark:border-[#494949] text-[#C3C3C3] dark:text-[#494949] rounded-md">
                  ⌘
                </div>
                <div className="px-1.5 text-[16px] border border-[#E5E7EB] dark:border-[#494949] text-[#C3C3C3] dark:text-[#494949] rounded-md">
                  K
                </div>
              </div>
            </button>
          </div>

          {/* Chat List */}
          <ChatList
            filteredChats={filteredChats}
            selectedTagFilter={selectedTagFilter}
            projectTags={projectTags}
            clearFilter={clearFilter}
            handleFilterMenuClick={handleFilterMenuClick}
            user={user}
            typingUsers={typingUsers}
            handleChatClick={handleChatClick}
            editingChatId={editingChatId}
            editValue={editValue}
            setEditValue={setEditValue}
            handleRenameSubmit={handleRenameSubmit}
            setEditingChatId={setEditingChatId}
            handleMenuClick={handleMenuClick}
            removeTagFromChat={removeTagFromChat}
          />
        </div>
      </div>

      {/* Project Settings Modal */}
      <ProjectSettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        project={project}
        onProjectUpdated={handleProjectUpdated}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Chat"
        message="Are you sure you want to delete this chat? All context provided will be retained. This cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />

      {/* Delete Document Confirmation Modal */}
      <Modal
        isOpen={deleteDocumentModalOpen}
        onClose={() => {
          setDeleteDocumentModalOpen(false);
          setDocumentToDelete(null);
        }}
        onConfirm={() => {
          if (documentToDelete) {
            handleDeleteDocument(documentToDelete.id);
          }
        }}
        title="Delete Document"
        message={`Are you sure you want to delete "${documentToDelete?.name}"? This will remove the document from the project's AI context. This cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
      />

      {/* Tag Selection Modal */}
      {showTagModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-[#161616] rounded-lg p-6 w-96 max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[#181818] dark:text-[#EDEDED]">
                {isCreatingNewTag ? 'Create New Tag' : 'Select Tag'}
              </h3>
              <button
                onClick={() => {
                  setShowTagModal(false);
                  setSelectedChatForTagging(null);
                  setIsCreatingNewTag(false);
                  setNewTagName('');
                  setNewTagColor('#3B82F6');
                  setNewTagIcon('tag');
                }}
                className="text-[#999999] hover:text-[#181818] dark:text-[#999999] dark:hover:text-[#EDEDED]"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {!isCreatingNewTag ? (
              <div className="space-y-3">
                {/* Existing Tags */}
                {projectTags.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-[#181818] dark:text-[#EDEDED]">Existing Tags</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {projectTags.map((tag) => {
                        const IconComponent = getTagIcon(tag.icon);
                        return (
                          <button
                            key={tag.id}
                            onClick={() => handleTagSelectionWithState(tag.id)}
                            className="flex items-center gap-2 p-2 rounded-lg border border-[#C3C3C3] dark:border-[#444444] hover:bg-[#EDEDED] dark:hover:bg-[#161616] transition-colors"
                          >
                            <div
                              className="w-4 h-4 rounded-full flex items-center justify-center"
                              style={{ backgroundColor: tag.color }}
                            >
                              <IconComponent className="w-2.5 h-2.5 text-white" />
                            </div>
                            <span className="text-sm text-[#181818] dark:text-[#EDEDED] truncate">{tag.name}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Create New Tag Button */}
                <div className="pt-3 border-t border-[#C3C3C3] dark:border-[#444444]">
                  <button
                    onClick={() => setIsCreatingNewTag(true)}
                    className="w-full flex items-center justify-center gap-2 p-2 rounded-lg border-2 border-dashed border-[#C3C3C3] dark:border-[#444444] hover:border-[#999999] dark:hover:border-[#666666] transition-colors"
                  >
                    <PlusIcon className="w-4 h-4 text-[#999999] dark:text-[#999999]" />
                    <span className="text-sm text-[#999999] dark:text-[#999999]">Create New Tag</span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Tag Name Input */}
                <div>
                  <label className="block text-sm font-medium text-[#181818] dark:text-[#EDEDED] mb-1">
                    Tag Name
                  </label>
                  <input
                    type="text"
                    value={newTagName}
                    onChange={(e) => setNewTagName(e.target.value)}
                    placeholder="e.g., Design, Development, Bug Fix"
                    className="w-full px-3 py-2 border border-[#C3C3C3] dark:border-[#444444] rounded-lg bg-white dark:bg-[#161616] text-[#181818] dark:text-[#EDEDED] focus:outline-none focus:ring-2 focus:ring-blue-500"
                    autoFocus
                  />
                </div>

                {/* Icon Selection */}
                <div>
                  <label className="block text-sm font-medium text-[#181818] dark:text-[#EDEDED] mb-2">
                    Tag Icon
                  </label>
                  <div className="grid grid-cols-7 gap-2">
                    {tagIcons.map((iconOption) => {
                      const IconComponent = iconOption.icon;
                      return (
                        <button
                          key={iconOption.id}
                          type="button"
                          onClick={() => setNewTagIcon(iconOption.id)}
                          className={`w-8 h-8 rounded-lg border-2 flex items-center justify-center transition-all ${
                            newTagIcon === iconOption.id 
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                              : 'border-[#C3C3C3] dark:border-[#444444] hover:border-[#999999] dark:hover:border-[#666666]'
                          }`}
                          title={iconOption.name}
                        >
                          <IconComponent className="w-4 h-4 text-[#999999] dark:text-[#999999]" />
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Color Selection */}
                <div>
                  <label className="block text-sm font-medium text-[#181818] dark:text-[#EDEDED] mb-2">
                    Tag Color
                  </label>
                  <div className="grid grid-cols-5 gap-2">
                    {tagColors.map((color) => (
                      <button
                        key={color}
                        onClick={() => setNewTagColor(color)}
                        className={`w-8 h-8 rounded-full border-2 transition-all ${
                          newTagColor === color 
                            ? 'border-[#181818] dark:border-[#EDEDED] scale-110' 
                            : 'border-[#C3C3C3] dark:border-[#444444] hover:scale-105'
                        }`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 pt-2">
                  <button
                    onClick={() => {
                      setIsCreatingNewTag(false);
                      setNewTagName('');
                      setNewTagColor('#3B82F6');
                      setNewTagIcon('tag');
                    }}
                    className="flex-1 px-4 py-2 text-sm text-[#181818] dark:text-[#EDEDED] bg-[#EDEDED] dark:bg-[#161616] rounded-lg hover:bg-[#C3C3C3] dark:hover:bg-[#444444] transition-colors"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleCreateNewTagWithState}
                    disabled={!newTagName.trim()}
                    className="flex-1 px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Create & Apply
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Context Menu */}
      {activeMenu && (
        <ContextMenu
          isOpen={true}
          position={menuPosition}
          onClose={() => setActiveMenu(null)}
          items={[
            {
              label: 'Tag',
              icon: <FaTag className="w-4 h-4" />,
              onClick: (e) => handleTagClick(e, activeMenu)
            },
            {
              label: 'Rename',
              icon: <PencilIcon className="w-4 h-4" />,
              onClick: (e) => {
                const chat = chats.find(c => c._id === activeMenu);
                if (chat) handleRename(e, activeMenu, chat.title);
              }
            },
            {
              label: 'Delete',
              icon: <TrashIcon className="w-4 h-4" />,
              onClick: (e) => handleDelete(e, activeMenu),
              danger: true
            }
          ]}
        />
      )}

      {/* Filter Menu */}
      {showFilterMenu && (
        <ContextMenu
          isOpen={true}
          position={filterMenuPosition}
          onClose={() => setShowFilterMenu(false)}
          items={[
            {
              label: 'Show All Chats',
              onClick: () => clearFilter()
            },
            ...(projectTags.length > 0 ? [
              ...projectTags.map(tag => {
                const IconComponent = getTagIcon(tag.icon);
                return {
                  label: tag.name,
                  icon: (
                    <div
                      className="w-4 h-4 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: tag.color }}
                    >
                      <IconComponent className="w-2.5 h-2.5 text-white" />
                    </div>
                  ),
                  onClick: () => handleFilterByTag(tag.id)
                };
              })
            ] : [])
          ]}
        />
      )}
    </div>
  );
};

export default ProjectDashboard; 